"use client";

import React, { useRef, useEffect, useState } from "react";
import { BabyStepCard, Typography } from "@aph/ui-lib-humanx";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import currentFlowSelector from "@/components/PageHome/redux/selectors/currentFlow";
import { clearFlowData, setCurrentFlow } from "@/components/PageHome/redux/slice";
import { JourneyItem, OnboardingSectionComponent } from "@/components/PageHome/api/interface";
import { DeregisterRefType, RegisterRefType, STYLE_TYPE } from "@/components/PageHome/interfaces";
import OnboardingFlow from "@/components/PageHome/components/OnboardingSection/Modals/OnboardingFlow";
import { assetClickedEvent } from "@/helpers/TSDEvents";
import { NON_ONBOARDED_STATUS } from "@/helpers/constants";
import { getPlainTexts } from "@/helpers";
import { DeviceInfo } from "@/interface/deviceInfo";
import refererSelector from "@/redux/slice/global/selectors/refererSelector";
import styles from "./Onboarding.module.scss";

interface OnboardingSectionProps {
  data?: OnboardingSectionComponent;
  registerRef?: RegisterRefType;
  deregisterRef?: DeregisterRefType;
  assetPosition?: number;
  userAgent: DeviceInfo;
}

const OnboardingSection: React.FC<OnboardingSectionProps> = ({
  data,
  registerRef,
  deregisterRef,
  assetPosition,
  userAgent,
}) => {
  const { Title, Description, Journey } = data;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const onBoardingRef = useRef<HTMLElement>(null);
  const currentFlow = useAppSelector(currentFlowSelector);
  const navSrcSelector = useAppSelector(refererSelector);

  const dispatch = useAppDispatch();

  useEffect(() => {
    registerRef("onBoardingRef", onBoardingRef.current);

    // Cleanup on unmount
    return () => {
      deregisterRef("onBoardingRef");
    };
  }, [registerRef]);

  return (
    <>
      <section ref={onBoardingRef} id={"onBoardingSection"} className={`container spacingBetweenSections`}>
        <Typography component="h1" className={styles.landingTitle}>
          MomVerse - Preconception, Pregnancy & Motherhood
        </Typography>
        {Title && <h5 className={styles.title} dangerouslySetInnerHTML={{ __html: Title }} />}
        {Description && <div className={styles.description} dangerouslySetInnerHTML={{ __html: Description }} />}
        {Journey?.length > 0 && (
          <div className={styles.cardsWrapper}>
            {Journey.map((item: JourneyItem, index) => {
              const backgroundStyle: STYLE_TYPE = Object.values(STYLE_TYPE)[index % 4];

              return (
                <div key={item.id} className={styles.card}>
                  <BabyStepCard
                    className={styles.babyCard}
                    titleText={item.Title}
                    backgroundIconStyle={backgroundStyle}
                    onClick={(): void => {
                      assetClickedEvent({
                        asset_name: "Onboarding Flow Card",
                        asset_display_text: getPlainTexts(item?.Title),
                        asset_position: assetPosition,
                        asset_element_position: index + 1,
                        momverse_stage: NON_ONBOARDED_STATUS,
                        nav_src: navSrcSelector,
                      });

                      if (currentFlow !== item.Type) {
                        dispatch(clearFlowData());
                      }

                      dispatch(setCurrentFlow(item.Type));
                      setIsModalOpen(true);
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </section>
      <OnboardingFlow
        isOpen={isModalOpen}
        currentFlow={currentFlow}
        onClose={() => setIsModalOpen(false)}
        userAgent={userAgent}
      />
    </>
  );
};

export default OnboardingSection;
